// DAS-2110: LLM Model Usage & Credit Balance Dashboard
import React, { useState, useEffect } from 'react';
import { Modal, Spin, Tag, Card, Typography, Divider, Alert, Badge, Space, Tooltip } from 'antd';
import { CheckCircleOutlined, WarningOutlined, CloseCircleOutlined, ApiOutlined } from '@ant-design/icons';
import api from '../functions/api';

const { Title, Text } = Typography;

const providerColors = {
  'DeepSeek': '#1a73e8',
  'OpenAI': '#10a37f',
  'Google Gemini': '#ea4335',
  'Anthropic': '#d97706',
};

const BalanceBadge = ({ balance }) => {
  if (!balance) return <Tag>Unknown</Tag>;

  if (balance.balance !== undefined && balance.balance !== null) {
    const val = parseFloat(balance.balance);
    const color = val > 5 ? 'green' : val > 1 ? 'orange' : 'red';
    const icon = val > 1 ? <CheckCircleOutlined /> : <WarningOutlined />;
    return (
      <Tag icon={icon} color={color}>
        {balance.currency} {val.toFixed(2)}
      </Tag>
    );
  }

  if (balance.status === 'active') {
    return <Tag icon={<CheckCircleOutlined />} color="green">Active</Tag>;
  }
  if (balance.status === 'no_api_key') {
    return <Tag icon={<WarningOutlined />} color="orange">No API Key Set</Tag>;
  }
  if (balance.error) {
    return (
      <Tooltip title={balance.error}>
        <Tag icon={<CloseCircleOutlined />} color="red">Error</Tag>
      </Tooltip>
    );
  }
  return <Tag>{balance.note || 'Unknown'}</Tag>;
};

const ProviderCard = ({ provider, data }) => {
  const color = providerColors[provider] || '#666';
  return (
    <Card
      size="small"
      style={{ marginBottom: 12, borderLeft: `4px solid ${color}` }}
      title={
        <Space>
          <ApiOutlined style={{ color }} />
          <Text strong style={{ color }}>{provider}</Text>
          <Badge count={data.script_count} style={{ backgroundColor: color }} overflowCount={99} />
        </Space>
      }
      extra={<BalanceBadge balance={data.balance} />}
    >
      <div style={{ marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Models:</Text>
        <div style={{ marginTop: 4 }}>
          {data.models?.map(m => (
            <Tag key={m} style={{ marginBottom: 4 }}>{m}</Tag>
          ))}
        </div>
      </div>
      <Divider style={{ margin: '8px 0' }} />
      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Scripts ({data.script_count}):
        </Text>
        <div style={{ marginTop: 4, maxHeight: 200, overflowY: 'auto' }}>
          {data.scripts?.map(s => (
            <div key={s} style={{ fontFamily: 'monospace', fontSize: 11, padding: '2px 0', color: '#555' }}>
              {s}
            </div>
          ))}
        </div>
      </div>
      {data.balance?.note && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>{data.balance.note}</Text>
        </div>
      )}
    </Card>
  );
};

const LLMModelsModal = ({ isVisible, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isVisible) {
      setLoading(true);
      setError(null);
      api.get('/llm_models')
        .then(res => {
          setData(res.data);
        })
        .catch(err => {
          const msg = err?.response?.data?.error || err.message || 'Failed to fetch LLM data';
          setError(msg);
        })
        .finally(() => setLoading(false));
    }
  }, [isVisible]);

  const providers = data?.data?.providers || {};
  const scanTime = data?.scan_timestamp;
  const providerCount = Object.keys(providers).length;
  const totalScripts = Object.values(providers).reduce((sum, p) => sum + (p.script_count || 0), 0);

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      footer={null}
      width={720}
      title={
        <Space>
          <ApiOutlined />
          <span>LLM Model Usage & Credit Balance</span>
        </Space>
      }
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" tip="Loading LLM inventory..." />
        </div>
      )}

      {error && (
        <Alert
          type="warning"
          message="Could not load LLM data"
          description={error}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {!loading && data && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Space>
              <Tag color="blue">{providerCount} Provider{providerCount !== 1 ? 's' : ''}</Tag>
              <Tag color="purple">{totalScripts} Script{totalScripts !== 1 ? 's' : ''}</Tag>
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Last scan: {scanTime || 'N/A'}
            </Text>
          </div>

          {Object.entries(providers).map(([provider, pData]) => (
            <ProviderCard key={provider} provider={provider} data={pData} />
          ))}

          {providerCount === 0 && (
            <Alert
              type="info"
              message="No LLM providers found"
              description="Run DC_LLM_Model_Inventory.py to populate this data."
              showIcon
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default LLMModelsModal;
